import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Lock01 } from '@untitledui/icons';
import { isAxiosError } from 'axios';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { apiRequest } from '@/shared/api/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { AUTH_FORM_SIZE } from '@/shared/config/auth-form';
import { ROUTES } from '@/shared/config/constants';
import { KnowMatchLogo, ParticleSphere, TextLink } from '@/shared/ui';

const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const resetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Use no minimo 8 caracteres')
      .regex(PASSWORD_REGEX, 'Inclua 1 maiúscula, 1 número e 1 caractere especial')
      .max(128),
    confirm: z.string().min(1, 'Confirme a senha'),
  })
  .refine((data) => data.password === data.confirm, {
    path: ['confirm'],
    message: 'As senhas não coincidem',
  });

type ResetFormValues = z.infer<typeof resetSchema>;

type ResetState = 'idle' | 'success';

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? 'Não foi possível redefinir a senha';
  }
  return 'Não foi possível redefinir a senha';
};

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token')?.trim() ?? '', [searchParams]);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<ResetState>('idle');

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    mode: 'onBlur',
    defaultValues: { password: '', confirm: '' },
  });

  useEffect(() => {
    if (!token) {
      toast.error('Link inválido. Solicite outro pelo "Esqueci minha senha".');
    }
  }, [token]);

  const onSubmit = async (values: ResetFormValues) => {
    if (!token) return;
    setSubmitting(true);
    try {
      await apiRequest<void>({
        method: 'POST',
        url: ENDPOINTS.AUTH.RESET_PASSWORD,
        data: { token, password: values.password },
      });
      setState('success');
      toast.success('Senha redefinida. Você pode entrar agora.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const tokenAusente = token.length === 0;

  return (
    <main className="flex min-h-dvh w-full flex-col bg-white lg:grid lg:grid-cols-2">
      {/* Mobile only: logo no topo, estilo header de app. */}
      <header className="flex items-center justify-center px-6 pt-[calc(env(safe-area-inset-top)+24px)] pb-2 lg:hidden">
        <KnowMatchLogo className="h-16 w-auto text-gray-900" aria-label="Know-How Match" />
      </header>

      <aside className="relative hidden min-h-dvh flex-col overflow-hidden bg-black text-white lg:flex">
        <div className="relative z-10 flex items-center gap-3 p-10">
          <KnowMatchLogo className="h-9 w-auto text-white" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <ParticleSphere className="aspect-square h-full max-h-[120%] w-full max-w-[120%]" />
        </div>
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black via-black/70 to-transparent"
          aria-hidden="true"
        />
        <div className="relative z-10 mt-auto flex flex-col gap-3 p-10">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Defina sua nova senha
          </h2>
          <p className="max-w-md text-base leading-relaxed text-white/60">
            Use uma senha forte e exclusiva. Você será direcionado para o login em seguida.
          </p>
        </div>
      </aside>

      <section className="flex flex-1 items-center justify-center px-6 py-6 sm:px-10 lg:min-h-dvh lg:py-16">
        <div className="w-full max-w-md">
          <TextLink to={ROUTES.LOGIN} className="mb-6">
            <ArrowLeft className="size-4" aria-hidden="true" />
            Voltar para o login
          </TextLink>

          {state === 'success' ? (
            <div className="flex flex-col gap-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600">
                <CheckCircle className="size-6" aria-hidden="true" />
              </div>
              <header className="flex flex-col gap-2">
                <h1 className="text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">
                  Senha redefinida
                </h1>
                <p className="text-base text-gray-500">
                  Sua senha foi atualizada. Use a nova senha para entrar.
                </p>
              </header>
              <Button
                type="button"
                size={AUTH_FORM_SIZE}
                color="primary"
                onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
                className="w-full bg-black ring-black hover:bg-gray-900"
              >
                Ir para o login
              </Button>
            </div>
          ) : (
            <>
              <header className="flex flex-col gap-2">
                <h1 className="text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">
                  Nova senha
                </h1>
                <p className="text-base text-gray-500">
                  {tokenAusente
                    ? 'Link sem token. Volte e solicite um novo email.'
                    : 'Escolha uma senha forte que voce nao usa em outros servicos.'}
                </p>
              </header>

              <form
                className="mt-10 flex flex-col gap-5"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <Controller
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <Input
                      label="Nova senha"
                      type="password"
                      size={AUTH_FORM_SIZE}
                      icon={Lock01}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      autoFocus
                      isInvalid={!!errors.password}
                      hint={errors.password?.message}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="confirm"
                  render={({ field }) => (
                    <Input
                      label="Confirme a nova senha"
                      type="password"
                      size={AUTH_FORM_SIZE}
                      icon={Lock01}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      isInvalid={!!errors.confirm}
                      hint={errors.confirm?.message}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                  )}
                />

                <Button
                  type="submit"
                  size={AUTH_FORM_SIZE}
                  color="primary"
                  isDisabled={tokenAusente}
                  isLoading={submitting}
                  className="w-full bg-black ring-black hover:bg-gray-900 data-loading:bg-gray-900"
                >
                  Redefinir senha
                </Button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
};
